import styled from "styled-components";

// Shared styles for the App Data screens.

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  color: var(--main-font-color);
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

export const Card = styled.div`
  border: 1px solid var(--content-item-border-color);
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 14px;
  box-shadow: var(--content-item-box-shadow);
`;

export const Split = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 14px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const GroupList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 520px;
  overflow: auto;
`;

export const GroupRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: 1px solid ${(props) => (props.$active ? "var(--button-bg)" : "transparent")};
  border-radius: 6px;
  background-color: ${(props) => (props.$active ? "var(--sidebar-active-item-bg)" : "transparent")};
  color: var(--main-font-color);
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: var(--sidebar-active-item-bg);
  }
`;

export const Count = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: #a0a6b1;
`;

export const Truncate = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Table = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid var(--content-item-border-color);
  border-radius: 8px;
  overflow: hidden;
`;

export const Row = styled.button`
  display: grid;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-bottom: 1px solid var(--content-item-border-color);
  background-color: ${(props) => (props.$active ? "var(--sidebar-active-item-bg)" : "transparent")};
  color: var(--main-font-color);
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--sidebar-active-item-bg);
  }
`;

/** A row that may contain its own action buttons (so it must not be a <button>). */
export const DataRow = styled.div`
  display: grid;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 8px 10px;
  border-bottom: 1px solid var(--content-item-border-color);
  background-color: ${(props) => (props.$active ? "var(--sidebar-active-item-bg)" : "transparent")};
  color: var(--main-font-color);
  font-size: 12.5px;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--sidebar-active-item-bg);
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`;

export const HeadRow = styled(DataRow)`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0a6b1;
  cursor: default;

  &:hover {
    background-color: transparent;
  }
`;

export const Muted = styled.div`
  font-size: 12.5px;
  font-style: italic;
  color: #a0a6b1;
`;

export const Mono = styled.span`
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(props) => props.$tone ?? "#a0a6b1"};
  border: 1px solid ${(props) => props.$tone ?? "#a0a6b1"};
  background-color: ${(props) => `${props.$tone ?? "#a0a6b1"}1a`};
`;

export const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 11.5px;
  border: 1px solid ${(props) => (props.$active ? "var(--button-bg)" : "var(--main-border-color)")};
  border-radius: 6px;
  background: ${(props) => (props.$active ? "var(--sidebar-active-item-bg)" : "transparent")};
  color: ${(props) => (props.$danger ? "#e74c3c" : "var(--main-font-color)")};
  font-weight: ${(props) => (props.$active ? 700 : 400)};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 12.5px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  background-color: var(--button-bg);
  color: var(--button-font-color);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ErrorBanner = styled.div`
  padding: 9px 11px;
  font-size: 12.5px;
  border-radius: 6px;
  border: 1px solid #e74c3c;
  background-color: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
`;

export const Notice = styled.div`
  padding: 9px 11px;
  font-size: 12.5px;
  border-radius: 6px;
  border: 1px solid #2ecc71;
  background-color: rgba(46, 204, 113, 0.1);
  color: #2ecc71;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

export const Label = styled.label`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const fieldStyles = `
  width: 100%;
  padding: 6px 9px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

export const Input = styled.input`
  ${fieldStyles}
`;

export const Select = styled.select`
  ${fieldStyles}
  cursor: pointer;
`;

export const Hint = styled.div`
  font-size: 11.5px;
  font-style: italic;
  color: #a0a6b1;
`;
